import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import i18n from "../i18n";
import { playSound, wasHandled, toastFailed } from "../lib/sounds";   // wasHandled = déjà montré dans cet onglet
import { taskKey, taskEndMessage, taskViewPath, voiceLineFor, voiceLinesFor } from "../lib/taskEndWatch";

// Veilleur global des fins de tâche (PLAN_SONS_NOTIFICATION.md, Lot 1-bis) : monté une fois hors des routes, il suit
// les générations de l'utilisateur VUES en cours (`watch`) et sonne « terminé » / « erreur » quand le serveur les dit
// finies — même quand la page qui les suivait est fermée. Ce qui n'a jamais été vu en cours (action instantanée,
// tâche finie avant le chargement) ne sonne jamais. Les pages gardent la main sur leurs propres tâches : chaque fin
// attend GRACE_MS ; le son est dédoublonné par sa clé, le toast omis si une page de cet onglet l'a déjà montrée.
const POLL_ACTIVE_MS = 20000;
const POLL_IDLE_MS = 60000;
const MIN_GAP_MS = 5000;        // changements de page en rafale : un poll au plus toutes les 5 s (différé, jamais perdu)
const GRACE_MS = 15000;         // pollers de page : ≤ 10 s + latence
const WATCH_MAX = 150;          // = _WATCH_MAX côté serveur
const STORE_MAX_AGE_MS = 5 * 60 * 1000;   // rechargement : on reprend `watch` s'il est frais
const TOAST_ID = "mw-task-end";

export default function TaskEndWatcher({ api, userId }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pollSoonRef = useRef(null);

  useEffect(() => {
    if (!userId) return undefined;
    const storeKey = `mw_task_watch:${userId}`;
    let watch = [];          // ids en cours au tour précédent
    try {
      const saved = JSON.parse(sessionStorage.getItem(storeKey) || "null");
      if (saved && Date.now() - (saved.at || 0) < STORE_MAX_AGE_MS && Array.isArray(saved.watch)) watch = saved.watch;
    } catch { /* noop */ }
    const reported = new Map();   // id → horodatage : une fin n'est signalée qu'une fois
    let pending = [];             // { item, due }
    let timer = null;
    let graceTimer = null;
    let busy = false;
    let again = false;
    let lastPollAt = 0;
    let stopped = false;

    const flush = () => {
      graceTimer = null;
      if (stopped) return;
      const now = Date.now();
      const ready = pending.filter((p) => p.due <= now).map((p) => p.item);
      pending = pending.filter((p) => p.due > now);
      if (pending.length) graceTimer = setTimeout(flush, Math.max(0, Math.min(...pending.map((p) => p.due)) - now));
      if (!ready.length) return;
      try {
        // Toast : seulement pour ce qu'aucune page de cet onglet n'a déjà montré — calculé AVANT les sons, car
        // playSound(…, { key }) marque lui-même la clé comme montrée
        const items = ready.filter((it) => !wasHandled(taskKey(it)));
        // Son : clé par tâche — déjà jouée par une page ou un autre onglet, elle reste muette ici ; l'anti-rafale fait
        // un seul son pour plusieurs fins. Annonce : calculée sur ce qu'aucune page de l'onglet n'a annoncé
        const lines = voiceLinesFor(items);
        for (const it of ready) {
          const failed = it.status === "failed";
          // Montrée sans son par la page (toast SSE video_ready) : hors `items`, sa propre phrase
          const say = (failed ? lines.error : lines.done) || voiceLineFor(it.tool, failed);
          playSound(failed ? "error" : "done", { key: taskKey(it), say });
        }
        if (!items.length) return;
        const path = taskViewPath(items);
        const opts = { id: TOAST_ID, ...(path ? { action: { label: i18n.t("notifications.taskEnd.view"), onClick: () => navigateRef.current(path) } } : {}) };
        const message = taskEndMessage(items);
        // toastFailed avec une clé déjà prise : le toast d'erreur sans second son
        if (items.every((it) => it.status === "failed")) toastFailed(message, opts, { key: taskKey(items[0]) });
        else toast.success(message, opts);
      } catch { /* un signal ne doit jamais casser l'app */ }
    };

    const schedule = () => {
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(poll, watch.length ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };

    const pollSoon = () => {
      if (stopped) return;
      if (busy) { again = true; return; }
      clearTimeout(timer);
      timer = setTimeout(poll, Math.max(0, lastPollAt + MIN_GAP_MS - Date.now()));
    };

    async function poll() {
      if (busy || stopped) return;
      busy = true;
      lastPollAt = Date.now();
      try {
        const params = watch.length ? { watch: watch.join(",") } : {};
        const { data } = await axios.get(`${api}/notifications/tasks`, { params, timeout: 15000 });
        if (stopped) return;
        const active = Array.isArray(data?.active) ? data.active : [];
        watch = active.map((a) => a?.id).filter(Boolean).slice(0, WATCH_MAX);
        try { sessionStorage.setItem(storeKey, JSON.stringify({ watch, at: Date.now() })); } catch { /* noop */ }
        const now = Date.now();
        for (const [id, ts] of reported) if (now - ts > 60 * 60 * 1000) reported.delete(id);
        for (const it of Array.isArray(data?.finished) ? data.finished : []) {
          if (!it?.id || reported.has(it.id)) continue;
          reported.set(it.id, now);
          pending.push({ item: it, due: now + GRACE_MS });
        }
        if (pending.length && !graceTimer) graceTimer = setTimeout(flush, GRACE_MS);
      } catch {
        // Silencieux (jamais toast.error : son + spam) ; `watch` gardé pour le tour suivant
      } finally {
        busy = false;
        if (again) { again = false; pollSoon(); } else schedule();
      }
    }

    pollSoonRef.current = pollSoon;
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(graceTimer);
      pollSoonRef.current = null;
    };
  }, [api, userId]);

  // Changement de page : la tâche qu'on vient de quitter passe tout de suite sous surveillance (pas au montage :
  // le premier poll part déjà)
  const firstPathRef = useRef(true);
  useEffect(() => {
    if (firstPathRef.current) { firstPathRef.current = false; return; }
    pollSoonRef.current?.();
  }, [pathname]);

  return null;
}
